import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { shallow } from "zustand/shallow";
import Breadcrumb from "../../components/Breadcrumb";
import Pagination from "../../components/Pagination";
import useResponsiveLimit from "../../composables/useResponsiveLimit";
import { Role, useAuthStore } from "../../stores/auth";
import { useInspectionsStore, makePageKey } from "../../stores/inspections";
import { useNatravStore } from "../../stores/natrav";
import { useNominatimStore } from "../../stores/nominatim";
import { useSettingsStore } from "../../stores/settings";
import { useUserStore } from "../../stores/users";
import InspectionsMapTab, { UNKNOWN_TYPE } from "./InspectionsMapTab";

const VALID_VIEWS = ["list", "map"];

const DEVICE_COLOR_PALETTE = [
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#bcbd22",
  "#17becf",
  "#ffd700",
  "#000075",
  "#006d77",
];
const DEVICE_COLOR_FALLBACK = "#6c757d";
const deviceColorCache = new Map();

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const filterSchema = Yup.object().shape({
  month: Yup.string()
    .required()
    .matches(/^\d{4}-\d{2}$/, "inspections.month_invalid"),
  inspector: Yup.string(),
});

const COLUMNS = [
  { key: "customer", sortable: true },
  { key: "property", sortable: true },
  { key: "device_type", sortable: true, nowrap: true },
  { key: "quantity", sortable: true, nowrap: true },
  { key: "next_inspection", sortable: true, nowrap: true },
  { key: "last_inspection", sortable: true, nowrap: true },
  { key: "interval", sortable: true, nowrap: true },
  { key: "inspector", sortable: true, nowrap: true },
];

function SortableHeader({ column, label, sortBy, sortDir, onSort, nowrap }) {
  const style = { cursor: "pointer", userSelect: "none" };
  const icon = sortBy === column ? <i className={`fa fa-sort-${sortDir === "asc" ? "up" : "down"} ms-1`} /> : null;

  return (
    <th style={style} onClick={() => onSort(column)} className={nowrap ? "text-nowrap" : undefined}>
      {label} {icon}
    </th>
  );
}

function Inspections() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [queryParams, setQueryParams] = useSearchParams();

  const [hasRole] = useAuthStore((state) => [state.hasRole], shallow);
  const isAdmin = hasRole(Role.ADMINISTRATOR);
  const canViewInspections = isAdmin || hasRole(Role.MANAGER);
  const [features, fetchFeatures] = useSettingsStore(
    (state) => [state.features, state.fetchFeatures],
    shallow,
  );

  const [byIds, pageCache, isLoading, storeTotal, fetchInspections, mapData, mapLoading] = useInspectionsStore(
    (state) => [
      state.byIds,
      state.pageCache,
      state.isLoading,
      state.total,
      state.fetchInspections,
      state.mapData,
      state.mapLoading,
    ],
    shallow,
  );

  const [tunnelAvailable, fetchTunnelStatus] = useNatravStore(
    (state) => [state.available, state.fetchStatus],
    shallow,
  );

  const [usersByIds, usersAllIds, fetchUsers] = useUserStore(
    (state) => [state.byIds, state.allIds, state.fetchSome],
    shallow,
  );

  const technicians = useMemo(
    () => usersAllIds.map((id) => usersByIds[id]).filter((u) => u?.roles?.some((r) => r.name === Role.TECHNICIAN)),
    [usersByIds, usersAllIds],
  );

  const defaultMonth = currentMonth();
  const defaultLimit = useResponsiveLimit(20);
  const page = parseInt(queryParams.get("page")) || 0;
  const limit = parseInt(queryParams.get("limit")) || defaultLimit;
  const offset = page * limit;
  const sortBy = queryParams.get("sort_by") || "next_inspection";
  const sortDir = queryParams.get("sort_dir") || "asc";
  const rawView = queryParams.get("view");
  const view = VALID_VIEWS.includes(rawView) ? rawView : "list";
  const isMapView = view === "map";

  const formik = useFormik({
    initialValues: {
      month: queryParams.get("month") || defaultMonth,
      inspector: queryParams.get("inspector") || "",
    },
    validationSchema: filterSchema,
    validateOnChange: true,
    validateOnBlur: false,
    onSubmit: () => {},
  });

  const fetchParams = {
    inspector: formik.values.inspector ? [formik.values.inspector] : undefined,
    from: formik.values.month,
    to: formik.values.month,
    sort_by: sortBy,
    sort_dir: sortDir,
    limit,
    offset,
  };
  const mapFetchParams = useMemo(
    () => ({
      inspector: formik.values.inspector ? [formik.values.inspector] : undefined,
      from: formik.values.month,
      to: formik.values.month,
      sort_by: sortBy,
      sort_dir: sortDir,
    }),
    [formik.values.inspector, formik.values.month, sortBy, sortDir],
  );
  const pageKey = makePageKey(fetchParams);
  const cached = pageCache[pageKey];
  const allIds = cached?.allIds ?? [];
  const total = cached?.total ?? storeTotal;
  const items = useMemo(() => allIds.map((id) => byIds[id]), [allIds, byIds]);

  const updateParams = (overrides) => {
    const params = new URLSearchParams();
    const month = overrides.month ?? formik.values.month;
    const inspector = overrides.inspector ?? formik.values.inspector;

    params.set("month", month);
    if (inspector) params.set("inspector", inspector);
    params.set("sort_by", overrides.sort_by ?? sortBy);
    params.set("sort_dir", overrides.sort_dir ?? sortDir);
    if (overrides.page !== undefined) {
      params.set("page", String(overrides.page));
    }
    const nextView = overrides.view ?? view;
    if (nextView && nextView !== "list") params.set("view", nextView);
    setQueryParams(params);
  };

  const handleTabChange = (nextView) => (e) => {
    e.preventDefault();
    if (nextView === view) return;
    updateParams({ view: nextView });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    formik.handleChange(e);
    updateParams({ ...formik.values, [name]: value, page: 0 });
  };

  const handleSort = (column) => {
    const newDir = sortBy === column && sortDir === "asc" ? "desc" : "asc";
    updateParams({ sort_by: column, sort_dir: newDir, page: 0 });
  };

  const [selectedYear, selectedMonthPart] = (
    formik.values.month || defaultMonth
  ).split("-");

  const [selectedDeviceType, setSelectedDeviceType] = useState(null);

  const mapMarkers = useMemo(
    () => mapData.filter((item) => item.lat != null && item.lon != null),
    [mapData],
  );

  const { mapDeviceTypes, mapColorByType, mapHasUnknown } = useMemo(() => {
    const unique = Array.from(
      new Set(mapMarkers.map((m) => m.device_type).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b));

    unique.forEach((type) => {
      if (deviceColorCache.has(type)) return;
      const slot = deviceColorCache.size % DEVICE_COLOR_PALETTE.length;
      deviceColorCache.set(type, DEVICE_COLOR_PALETTE[slot]);
    });

    const colorByType = {};
    unique.forEach((type) => {
      colorByType[type] = deviceColorCache.get(type);
    });

    return {
      mapDeviceTypes: unique,
      mapColorByType: colorByType,
      mapHasUnknown: mapMarkers.some((m) => !m.device_type),
    };
  }, [mapMarkers]);

  const effectiveSelectedDeviceType =
    selectedDeviceType === null
      ? null
      : selectedDeviceType === UNKNOWN_TYPE
        ? mapHasUnknown
          ? UNKNOWN_TYPE
          : null
        : mapDeviceTypes.includes(selectedDeviceType)
          ? selectedDeviceType
          : null;

  const handleLegendTypeClick = (type) => {
    setSelectedDeviceType((prev) => (prev === type ? null : type));
  };

  const selectedDeviceTypeLabel =
    effectiveSelectedDeviceType === null
      ? t("menu.all")
      : effectiveSelectedDeviceType === UNKNOWN_TYPE
        ? t("inspections.map_legend_unknown")
        : effectiveSelectedDeviceType;

  const selectedDeviceTypeColor =
    effectiveSelectedDeviceType === null
      ? null
      : effectiveSelectedDeviceType === UNKNOWN_TYPE
        ? DEVICE_COLOR_FALLBACK
        : mapColorByType[effectiveSelectedDeviceType];

  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    const years = [];
    for (let y = now - 2; y <= now + 2; y += 1) years.push(String(y));
    if (selectedYear && !years.includes(selectedYear)) {
      years.push(selectedYear);
      years.sort();
    }
    return years;
  }, [selectedYear]);

  const monthOptions = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(i18n.language, { month: "long" });
    return Array.from({ length: 12 }, (_, i) => ({
      value: String(i + 1).padStart(2, "0"),
      label: formatter.format(new Date(2000, i, 1)),
    }));
  }, [i18n.language]);

  const setMonthValue = (next) => {
    formik.setFieldValue("month", next);
    updateParams({ month: next, page: 0 });
  };

  const handleYearChange = (e) => {
    setMonthValue(`${e.target.value}-${selectedMonthPart}`);
  };

  const handleMonthChange = (e) => {
    setMonthValue(`${selectedYear}-${e.target.value}`);
  };

  useEffect(() => {
    if (!canViewInspections) {
      navigate("/delivery_notes", { replace: true });
    }
  }, [canViewInspections, navigate]);

  useEffect(() => {
    fetchFeatures();
    fetchTunnelStatus();
    useNominatimStore.getState().fetchHealth();
    fetchUsers({ limit: 100, offset: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isValidMonth = (v) => /^\d{4}-\d{2}$/.test(v);

  useEffect(() => {
    if (!canViewInspections || !features.handel || !tunnelAvailable) return;
    if (!isValidMonth(formik.values.month)) return;
    fetchInspections(fetchParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features.handel, tunnelAvailable, formik.values.month, formik.values.inspector, sortBy, sortDir, limit, page]);

  if (!canViewInspections) return null;

  return (
    <div>
      <Breadcrumb items={[{ label: t("inspections.title") }]} />

      {!features.handel && (
        <div className="alert alert-warning mb-3">
          <i className="fa fa-exclamation-triangle me-2" />
          {t("customers.search_unavailable")}
        </div>
      )}

      {features.handel && tunnelAvailable === false && (
        <div className="alert alert-warning mb-3">
          <i className="fa fa-exclamation-triangle me-2" />
          {t("inspections.tunnel_unavailable")}
        </div>
      )}

      {features.handel && (
        <div className="card border-0">
          <ul className="nav nav-tabs nav-tabs-v2 px-3 pt-2">
            <li className="nav-item me-2">
              <a
                href="#listTab"
                className={`nav-link px-2${view === "list" ? " active" : ""}`}
                onClick={handleTabChange("list")}
              >
                {t("inspections.tab_list")}
              </a>
            </li>
            <li className="nav-item me-2">
              <a
                href="#mapTab"
                className={`nav-link px-2${view === "map" ? " active" : ""}`}
                onClick={handleTabChange("map")}
              >
                {t("inspections.tab_map")}
              </a>
            </li>
          </ul>

          <div className="card-body pb-0">
            <div className="row g-2 mb-3">
              <div className="col-6 col-lg-2">
                <label className="form-label">{t("inspections.month")}</label>
                <select
                  className="form-select"
                  value={selectedMonthPart}
                  onChange={handleMonthChange}
                >
                  {monthOptions.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-6 col-lg-1">
                <label className="form-label">{t("inspections.year")}</label>
                <select
                  className="form-select"
                  value={selectedYear}
                  onChange={handleYearChange}
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className={`col-12 ${isMapView ? "col-lg-5" : "col-lg-9"}`}>
                <label className="form-label">{t("inspections.inspector")}</label>
                <select
                  name="inspector"
                  className="form-select"
                  value={formik.values.inspector}
                  onChange={handleFilterChange}
                >
                  <option value="">{t("menu.all")}</option>
                  {technicians.map((u) => (
                    <option key={u.id} value={u.number || u.name}>
                      {u.name}
                      {u.number ? ` (${u.number})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              {isMapView && (
                <div className="col-12 col-lg-4" aria-label={t("inspections.map_legend")}>
                  <label className="form-label" htmlFor="map-device-type-filter">
                    {t("inspections.device_type")}
                  </label>
                  <div className="dropdown mb-1">
                    <button
                      id="map-device-type-filter"
                      type="button"
                      className="btn btn-outline-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      disabled={mapLoading}
                    >
                      <span className="d-inline-flex align-items-center w-100 overflow-hidden" style={{ gap: "0.35rem" }}>
                        {selectedDeviceTypeColor !== null && (
                          <span
                            aria-hidden="true"
                            style={{
                              display: "inline-block",
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              background: selectedDeviceTypeColor,
                              border: "1px solid #fff",
                              boxShadow: "0 0 2px rgba(0,0,0,0.3)",
                              flex: "0 0 auto",
                            }}
                          />
                        )}
                        <span className="text-truncate" title={selectedDeviceTypeLabel}>
                          {selectedDeviceTypeLabel}
                        </span>
                      </span>
                    </button>

                    <ul className="dropdown-menu w-100 overflow-hidden" aria-labelledby="map-device-type-filter">
                      <li>
                        <button
                          type="button"
                          className={`dropdown-item${effectiveSelectedDeviceType === null ? " active" : ""}`}
                          onClick={() => setSelectedDeviceType(null)}
                        >
                          {t("menu.all")}
                        </button>
                      </li>
                      {mapDeviceTypes.map((type) => (
                        <li key={type}>
                          <button
                            type="button"
                            className={`dropdown-item d-flex align-items-center w-100 text-start${
                              effectiveSelectedDeviceType === type ? " active" : ""
                            }`}
                            style={{ gap: "0.35rem", overflow: "hidden" }}
                            onClick={() => handleLegendTypeClick(type)}
                            title={type}
                          >
                            <span
                              aria-hidden="true"
                              style={{
                                display: "inline-block",
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                background: mapColorByType[type],
                                border: "1px solid #fff",
                                boxShadow: "0 0 2px rgba(0,0,0,0.3)",
                                flex: "0 0 auto",
                              }}
                            />
                            <span className="text-truncate" style={{ minWidth: 0 }}>
                              {type}
                            </span>
                          </button>
                        </li>
                      ))}
                      {mapHasUnknown && (
                        <li>
                          <button
                            type="button"
                            className={`dropdown-item d-flex align-items-center w-100 text-start${
                              effectiveSelectedDeviceType === UNKNOWN_TYPE ? " active" : ""
                            }`}
                            style={{ gap: "0.35rem", overflow: "hidden" }}
                            onClick={() => handleLegendTypeClick(UNKNOWN_TYPE)}
                          >
                            <span
                              aria-hidden="true"
                              style={{
                                display: "inline-block",
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                background: DEVICE_COLOR_FALLBACK,
                                border: "1px solid #fff",
                                boxShadow: "0 0 2px rgba(0,0,0,0.3)",
                                flex: "0 0 auto",
                              }}
                            />
                            <span className="text-truncate" style={{ minWidth: 0 }}>
                              {t("inspections.map_legend_unknown")}
                            </span>
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="tab-content p-3">
            <div className={`tab-pane fade${view === "list" ? " show active" : ""}`} id="listTab">
            <div className={`table-responsive mb-3${isLoading ? " opacity-50" : ""}`}>
                  <table className="table table-hover table-panel align-middle mb-0">
                    <thead>
                      <tr>
                        {COLUMNS.map((col) => (
                          <SortableHeader
                            key={col.key}
                            column={col.key}
                            label={t(`inspections.${col.key}`)}
                            sortBy={sortBy}
                            sortDir={sortDir}
                            onSort={handleSort}
                            nowrap={col.nowrap}
                          />
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr key={item._id}>
                          <td>
                            {item.customer_nr && (
                              <div>
                                {item.slug ? (
                                  <Link to={`/sqlbase/inspections/${item.slug}/details`} className="fw-bold">
                                    {item.customer_nr}
                                  </Link>
                                ) : (
                                  item.customer_nr
                                )}
                              </div>
                            )}
                            {item.customer_name && <div>{item.customer_name}</div>}
                            {item.customer_address && <div>{item.customer_address}</div>}
                          </td>
                          <td>
                            {item.property_nr && (
                              <div>
                                {item.slug ? (
                                  <Link to={`/sqlbase/inspections/${item.slug}/details`} className="fw-bold">
                                    {item.property_nr}
                                  </Link>
                                ) : (
                                  item.property_nr
                                )}
                              </div>
                            )}
                            {item.property_name && <div>{item.property_name}</div>}
                            {item.property_address && <div>{item.property_address}</div>}
                          </td>
                          <td className="text-nowrap">{item.device_type || "-"}</td>
                          <td className="text-nowrap">{item.quantity ?? "-"}</td>
                          <td className="text-nowrap">{item.next_inspection || "-"}</td>
                          <td className="text-nowrap">{item.last_inspection || "-"}</td>
                          <td className="text-nowrap">{item.interval ? `${item.interval} ${t("common.months")}` : "-"}</td>
                          <td className="text-nowrap">{item.inspector || "-"}</td>
                        </tr>
                      ))}
                      {!isLoading && items.length === 0 && (
                        <tr>
                          <td colSpan={COLUMNS.length} className="text-center text-muted py-4">
                            {t("inspections.no_results")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

            {isLoading && (
              <div className="text-center py-2">
                <div className="spinner-border spinner-border-sm" role="status" />
              </div>
            )}

            <Pagination
              basePath="/sqlbase/inspections"
              page={page}
              limit={limit}
              total={total}
              queryParams={queryParams}
              label={t("inspections.title")}
            />
            </div>

            <div className={`tab-pane fade${view === "map" ? " show active" : ""}`} id="mapTab">
              <InspectionsMapTab
                fetchParams={mapFetchParams}
                active={isMapView}
                selectedDeviceType={effectiveSelectedDeviceType}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Inspections;
